import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";
import { Badge } from "@/components/ui/badge";
import { HighRiskBadge } from "../HighRiskBadge";
import { pickLabelColor } from "../../lib/contrastLabel";

// Phase 12 — token-aware chart palette (calm, restrained)
export const BRAND_COLORS = {
  // surfaces (use CSS variables in Panel; these are fallbacks for charts)
  paper: "var(--surface)",
  border: "var(--border-subtle)",
  // text
  text: "var(--page-fg)",
  textSoft: "var(--page-fg-secondary)",
  muted: "var(--page-fg-muted)",
  // brand
  teal: "#1E3A5F",       // Phase 12 — brand navy as primary
  tealLight: "#3B82F6",  // accent blue
  tealDark: "#0F2340",
  // accents
  gold: "#D6B16A",
  goldLight: "#E8CB8E",
  // semantic
  red: "#E5484D",
  orange: "#F76808",
  yellow: "#F5D90A",
  lime: "#84C170",
  forest: "#5EAB45",
  blue: "#3B82F6",
  purple: "#8B5CF6",
  magenta: "#E12AFB",
  charcoal: "var(--page-fg)",
  gray: "var(--page-fg-muted)",
};

// Status → dot-ring palette (Phase 4b: 6 statuses)
const STATUS_COLOR = {
  New: BRAND_COLORS.blue,
  "In Progress": BRAND_COLORS.teal,
  "Under Investigation": BRAND_COLORS.purple,
  "Pending Follow-up": BRAND_COLORS.magenta,
  Escalated: BRAND_COLORS.red,
  Closed: BRAND_COLORS.forest,
  // Legacy alias kept defensively (should not appear post-migration)
  Open: BRAND_COLORS.blue,
};

const STATUS_LIST = [
  "New",
  "In Progress",
  "Under Investigation",
  "Pending Follow-up",
  "Escalated",
  "Closed",
];

// Pie palette draws from full dot-ring spectrum
const PIE_PALETTE = [
  BRAND_COLORS.teal,
  BRAND_COLORS.gold,
  BRAND_COLORS.purple,
  BRAND_COLORS.lime,
  BRAND_COLORS.magenta,
  BRAND_COLORS.blue,
  BRAND_COLORS.orange,
];

const GRID_STROKE = "var(--recharts-grid)";
const AXIS_FILL = "var(--recharts-axis-fg)";

function TooltipCard({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-xl border px-3 py-2"
      style={{
        backgroundColor: "var(--recharts-tooltip-bg)",
        borderColor: "var(--recharts-tooltip-border)",
        boxShadow: "var(--recharts-tooltip-shadow)",
      }}
    >
      {label && (
        <p
          className="mb-1 text-[11px] font-semibold uppercase tracking-wide"
          style={{ color: "var(--recharts-axis-fg)" }}
        >
          {label}
        </p>
      )}
      {payload.map((p, idx) => (
        <div key={idx} className="flex items-center gap-2 text-xs tabular-nums">
          {p.color && (
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ backgroundColor: p.color }}
            />
          )}
          <span style={{ color: "var(--page-fg-secondary)" }}>{p.name}:</span>
          <span
            className="font-medium"
            style={{ color: "var(--recharts-tooltip-fg)" }}
          >
            {p.value}
          </span>
        </div>
      ))}
    </div>
  );
}

function Panel({ title, subtitle, children, rightSlot, testid }) {
  return (
    <div data-testid={testid} className="brand-card flex flex-col p-6">
      <div className="mb-4 flex items-start justify-between gap-2">
        <div>
          <h3
            className="text-sm font-semibold"
            style={{ color: "var(--page-fg)", letterSpacing: "-0.005em" }}
          >
            {title}
          </h3>
          {subtitle && (
            <p
              className="mt-0.5 text-xs"
              style={{ color: "var(--page-fg-secondary)" }}
            >
              {subtitle}
            </p>
          )}
        </div>
        {rightSlot}
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function EmptyPanel({ label = "No data for current filters." }) {
  return (
    <div className="flex h-full min-h-[180px] flex-col items-center justify-center gap-2 text-center">
      <div
        className="flex h-10 w-10 items-center justify-center rounded-xl"
        style={{
          backgroundColor: "var(--surface-2)",
          border: "1px solid var(--border-subtle)",
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          style={{ color: "var(--page-fg-muted)" }}
        >
          <path d="M3 12h18M12 3v18" strokeLinecap="round" />
        </svg>
      </div>
      <p className="text-sm" style={{ color: "var(--page-fg-secondary)" }}>
        {label}
      </p>
    </div>
  );
}

function LoadingPanel() {
  return (
    <div className="flex h-full min-h-[180px] flex-col gap-3">
      <div className="skeleton-shimmer h-3 w-32" />
      <div className="skeleton-shimmer h-3 w-48" />
      <div className="skeleton-shimmer mt-2 h-32 w-full" />
    </div>
  );
}

// ---------- Issue Breakdown ----------

export function IssueBreakdownChart({ data, loading, onDrill }) {
  if (loading) {
    return (
      <Panel title="Most common ER issues" subtitle="By issue type" testid="panel-issue-breakdown">
        <LoadingPanel />
      </Panel>
    );
  }
  const items = data?.items || [];
  const chartData = items.map((i) => ({ name: i.issue_type, count: i.total }));
  return (
    <Panel
      title="Most common ER issues"
      subtitle="Click a bar to filter cases by that issue type"
      testid="panel-issue-breakdown"
    >
      {chartData.length === 0 ? (
        <EmptyPanel />
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} horizontal={false} />
            <XAxis type="number" tick={{ fill: BRAND_COLORS.muted, fontSize: 11 }} allowDecimals={false} />
            <YAxis
              type="category"
              dataKey="name"
              width={140}
              tick={{ fill: BRAND_COLORS.textSoft, fontSize: 12 }}
            />
            <Tooltip content={<TooltipCard />} cursor={{ fill: "rgba(20,184,184,0.08)" }} />
            <Bar dataKey="count" radius={[0, 8, 8, 0]}>
              {chartData.map((entry) => (
                <Cell
                  key={entry.name}
                  fill={BRAND_COLORS.teal}
                  cursor="pointer"
                  data-testid={`issue-bar-${entry.name}`}
                  onClick={() => onDrill?.({ issue_types: [entry.name] })}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
      {items.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1 border-t hairline pt-3">
          {items.slice(0, 5).map((it) => (
            <button
              key={it.issue_type}
              data-testid={`issue-drill-${it.issue_type}`}
              onClick={() => onDrill?.({ issue_types: [it.issue_type] })}
              className="rounded-full border hairline px-2.5 py-0.5 text-[11px] font-medium text-[color:var(--page-fg-secondary)] transition-colors hover:bg-[color:var(--surface)] hover:text-[color:var(--page-fg)]"
              style={{ backgroundColor: "var(--surface-2)" }}
            >
              {it.issue_type} · {it.total}
            </button>
          ))}
        </div>
      )}
    </Panel>
  );
}

// ---------- Service Line Distribution (donut) ----------

export function ServiceLineDistributionChart({ data, loading, onDrill }) {
  if (loading) {
    return <Panel title="Distribution across service lines" testid="panel-sl-distribution"><LoadingPanel /></Panel>;
  }
  const items = data?.items || [];
  const total = data?.total || 0;
  return (
    <Panel
      title="Distribution across service lines"
      subtitle={total ? `${total} cases in selected range` : "—"}
      testid="panel-sl-distribution"
    >
      {items.length === 0 ? (
        <EmptyPanel />
      ) : (
        <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-[1fr_180px]">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={items}
                dataKey="count"
                nameKey="service_line"
                innerRadius={56}
                outerRadius={90}
                paddingAngle={3}
              >
                {items.map((entry, i) => (
                  <Cell
                    key={entry.service_line}
                    fill={PIE_PALETTE[i % PIE_PALETTE.length]}
                    stroke="var(--surface)"
                    strokeWidth={2}
                    cursor="pointer"
                    data-testid={`sl-pie-${entry.service_line}`}
                    onClick={() => onDrill?.({ service_lines: [entry.service_line] })}
                  />
                ))}
              </Pie>
              <Tooltip content={<TooltipCard />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5">
            {items.map((it, i) => (
              <button
                key={it.service_line}
                onClick={() => onDrill?.({ service_lines: [it.service_line] })}
                data-testid={`sl-legend-${it.service_line}`}
                className="flex w-full items-center justify-between rounded-lg px-2 py-1 text-left text-xs transition-colors hover:bg-[color:var(--surface-2)]"
              >
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-sm"
                    style={{
                      backgroundColor: PIE_PALETTE[i % PIE_PALETTE.length],
                      boxShadow: `0 0 6px ${PIE_PALETTE[i % PIE_PALETTE.length]}`,
                    }}
                  />
                  <span className="font-medium text-[color:var(--page-fg)]">{it.service_line}</span>
                </div>
                <span className="text-[color:var(--page-fg-secondary)]">
                  {it.count} · {it.percentage}%
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </Panel>
  );
}

// ---------- ER Case Rate ----------

export function CaseRateChart({ data, loading, onDrill }) {
  if (loading) {
    return <Panel title="ER Case Rate per service line" testid="panel-case-rate"><LoadingPanel /></Panel>;
  }
  const items = data?.items || [];
  const chartData = items.map((i) => ({
    name: i.service_line,
    rate: i.rate_per_100 ?? 0,
    count: i.count,
    headcount: i.headcount ?? 0,
  }));
  return (
    <Panel
      title="ER case rate per service line"
      subtitle="Cases per 100 employees · click a bar or card to drill"
      testid="panel-case-rate"
    >
      {items.length === 0 ? (
        <EmptyPanel />
      ) : (
        <>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
              <XAxis dataKey="name" tick={{ fill: BRAND_COLORS.textSoft, fontSize: 12 }} />
              <YAxis tick={{ fill: BRAND_COLORS.muted, fontSize: 11 }} allowDecimals />
              <Tooltip content={<TooltipCard />} cursor={{ fill: "rgba(214,177,106,0.08)" }} />
              <Bar dataKey="rate" name="Rate / 100" radius={[6, 6, 0, 0]}>
                {chartData.map((entry) => (
                  <Cell
                    key={entry.name}
                    fill={BRAND_COLORS.gold}
                    cursor="pointer"
                    data-testid={`case-rate-bar-${entry.name}`}
                    onClick={() => onDrill?.({ service_lines: [entry.name] })}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-2 grid grid-cols-5 gap-1 text-[11px]">
            {items.map((i) => (
              <button
                key={i.service_line}
                onClick={() => onDrill?.({ service_lines: [i.service_line] })}
                data-testid={`case-rate-row-${i.service_line}`}
                className="rounded-lg border hairline px-2 py-1.5 text-center transition-colors hover:bg-[color:var(--surface)]"
                style={{ backgroundColor: "var(--surface-2)" }}
              >
                <p className="font-medium text-[color:var(--page-fg)]">{i.service_line}</p>
                <p className="text-[color:var(--page-fg-secondary)]">
                  {i.count} / {i.headcount ?? "—"}
                </p>
                <p className="font-semibold tabular-nums" style={{ color: "var(--page-fg)" }}>
                  {i.rate_per_100 != null ? `${i.rate_per_100}%` : "—"}
                </p>
              </button>
            ))}
          </div>
        </>
      )}
    </Panel>
  );
}

// ---------- Volume Trend ----------

export function VolumeTrendChart({ data, loading, granularity, onGranularityChange }) {
  const rightSlot = (
    <div
      className="flex items-center gap-1 rounded-xl border hairline p-1"
      style={{ backgroundColor: "var(--surface-2)" }}
    >
      {["weekly", "monthly"].map((g) => (
        <button
          key={g}
          onClick={() => onGranularityChange?.(g)}
          data-testid={`volume-granularity-${g}`}
          className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-colors ${
            granularity === g
              ? "bg-[color:#1E3A5F] text-white"
              : "text-[color:var(--page-fg-secondary)] hover:bg-[color:var(--surface-2)]"
          }`}
        >
          {g === "weekly" ? "Weekly" : "Monthly"}
        </button>
      ))}
    </div>
  );

  if (loading) {
    return (
      <Panel title="Case volume trend" testid="panel-volume-trend" rightSlot={rightSlot}>
        <LoadingPanel />
      </Panel>
    );
  }

  const series = data?.series || [];

  return (
    <Panel
      title="Case volume trend"
      subtitle={`${series.length} ${granularity === "weekly" ? "weeks" : "months"} covered`}
      testid="panel-volume-trend"
      rightSlot={rightSlot}
    >
      {series.length === 0 ? (
        <EmptyPanel />
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={series} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
            <XAxis dataKey="period" tick={{ fill: BRAND_COLORS.muted, fontSize: 11 }} />
            <YAxis tick={{ fill: BRAND_COLORS.muted, fontSize: 11 }} allowDecimals={false} />
            <Tooltip content={<TooltipCard />} />
            <Line
              type="monotone"
              dataKey="count"
              name="Cases reported"
              stroke={BRAND_COLORS.teal}
              strokeWidth={2.5}
              dot={{ r: 4, fill: BRAND_COLORS.teal, stroke: "var(--surface)", strokeWidth: 2 }}
              activeDot={{ r: 6, fill: BRAND_COLORS.tealLight }}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Panel>
  );
}

// ---------- Time to Resolution ----------

export function TimeToResolutionChart({ data, loading }) {
  if (loading) {
    return <Panel title="Time to resolution" testid="panel-ttr"><LoadingPanel /></Panel>;
  }
  const hist = data?.histogram || [];
  return (
    <Panel
      title="Time to resolution"
      subtitle={
        data?.sample_size
          ? `${data.sample_size} closed case${data.sample_size === 1 ? "" : "s"} in range`
          : "No closed cases in range"
      }
      testid="panel-ttr"
    >
      <div
        className="mb-3 flex items-center gap-6 rounded-xl border hairline px-3 py-2"
        style={{ backgroundColor: "rgba(20, 184, 184, 0.06)" }}
      >
        <div>
          <p className="text-[11px] uppercase tracking-wide text-[color:var(--page-fg-secondary)]">Mean</p>
          <p className="text-xl font-semibold text-[color:var(--page-fg)]" data-testid="ttr-mean">
            {data?.mean != null ? `${data.mean}d` : "—"}
          </p>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wide text-[color:var(--page-fg-secondary)]">Median</p>
          <p className="text-xl font-semibold text-[color:var(--page-fg)]" data-testid="ttr-median">
            {data?.median != null ? `${data.median}d` : "—"}
          </p>
        </div>
      </div>
      {hist.every((b) => b.count === 0) ? (
        <EmptyPanel label="Not enough data to bucket." />
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={hist} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
            <XAxis dataKey="bucket" tick={{ fill: BRAND_COLORS.textSoft, fontSize: 11 }} />
            <YAxis tick={{ fill: BRAND_COLORS.muted, fontSize: 11 }} allowDecimals={false} />
            <Tooltip content={<TooltipCard />} />
            <Bar dataKey="count" name="Cases" fill={BRAND_COLORS.tealLight} radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Panel>
  );
}

// ---------- Status Distribution (stacked bar) ----------

export function StatusDistributionChart({ data, loading, onDrill }) {
  if (loading) {
    return <Panel title="Open vs closed cases" testid="panel-status-dist"><LoadingPanel /></Panel>;
  }
  const counts = data?.counts || {};
  const series = data?.trend || [];
  const statuses = STATUS_LIST;
  return (
    <Panel
      title="Open vs closed cases"
      subtitle="Current counts + monthly trend"
      testid="panel-status-dist"
    >
      <div className="mb-3 grid grid-cols-3 gap-2 md:grid-cols-6">
        {statuses.map((s) => (
          <button
            key={s}
            data-testid={`status-kpi-${s.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => onDrill?.({ statuses: [s] })}
            className="rounded-xl border hairline p-2 text-left transition-shadow hover:shadow-brand-sm"
            style={{ backgroundColor: "var(--surface-2)" }}
          >
            <p className="text-[10px] uppercase tracking-wide text-[color:var(--page-fg-secondary)]">{s}</p>
            <p
              className="text-lg font-semibold tabular-nums"
              style={{ color: "var(--page-fg)" }}
            >
              <span
                aria-hidden
                className="mr-1.5 inline-block h-2 w-2 rounded-full align-middle"
                style={{ backgroundColor: STATUS_COLOR[s] }}
              />
              {counts[s] ?? 0}
            </p>
          </button>
        ))}
      </div>
      {series.length === 0 ? (
        <EmptyPanel label="No trend data for this range." />
      ) : (
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={series} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
            <XAxis dataKey="period" tick={{ fill: BRAND_COLORS.muted, fontSize: 11 }} />
            <YAxis tick={{ fill: BRAND_COLORS.muted, fontSize: 11 }} allowDecimals={false} />
            <Tooltip content={<TooltipCard />} />
            <Legend iconSize={8} wrapperStyle={{ fontSize: 11, color: BRAND_COLORS.textSoft }} />
            {statuses.map((s) => (
              <Bar key={s} dataKey={s} stackId="a" fill={STATUS_COLOR[s]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}
    </Panel>
  );
}

// ---------- Repeat Cases ----------

export function RepeatCasesTables({ employeeData, deptData, loading, onDrillEmployee }) {
  if (loading) {
    return (
      <Panel
        title="Repeat cases"
        subtitle="Team members or departments with ≥ 2 cases"
        testid="panel-repeat-cases"
      >
        <LoadingPanel />
      </Panel>
    );
  }
  const emp = employeeData?.items || [];
  const dept = deptData?.items || [];
  return (
    <Panel
      title="Repeat cases"
      subtitle="Team members or departments with ≥ 2 cases in the filter range"
      testid="panel-repeat-cases"
    >
      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--page-fg-secondary)]">
            Top employees
          </p>
          <div className="space-y-1">
            {emp.length === 0 && <EmptyPanel label="No repeat employees." />}
            {emp.map((e) => (
              <button
                key={e.key}
                data-testid={`repeat-emp-row-${e.position_id || e.file_number}`}
                onClick={() => onDrillEmployee?.(e)}
                className="flex w-full items-center gap-3 rounded-xl border hairline px-3 py-2 text-left transition-colors hover:bg-[color:var(--surface)]"
                style={{ backgroundColor: "var(--surface-2)" }}
              >
                <span
                  className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold"
                  style={{ backgroundColor: BRAND_COLORS.teal, color: "#FFFFFF" }}
                >
                  {e.case_count}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-[color:var(--page-fg)]">{e.name}</p>
                  <p className="truncate text-[11px] text-[color:var(--page-fg-secondary)]">
                    #{e.position_id || e.file_number} · {e.service_line} · {e.department}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  {e.issue_types.slice(0, 2).map((t) => (
                    <Badge
                      key={t}
                      variant="secondary"
                      className="mr-1 border hairline bg-[color:var(--surface-2)] text-[10px] text-[color:var(--page-fg-secondary)]"
                    >
                      {t}
                    </Badge>
                  ))}
                  {e.issue_types.length > 2 && (
                    <Badge
                      variant="secondary"
                      className="border hairline bg-[color:var(--surface-2)] text-[10px] text-[color:var(--page-fg-secondary)]"
                    >
                      +{e.issue_types.length - 2}
                    </Badge>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--page-fg-secondary)]">
            Top departments
          </p>
          <div className="space-y-1">
            {dept.length === 0 && <EmptyPanel label="No repeat departments." />}
            {dept.map((d) => (
              <div
                key={d.key}
                data-testid={`repeat-dept-row-${d.department}-${d.service_line}`}
                className="flex items-center gap-3 rounded-xl border hairline px-3 py-2"
                style={{ backgroundColor: "var(--surface-2)" }}
              >
                <span
                  className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold tabular-nums"
                  style={{
                    backgroundColor: BRAND_COLORS.gold,
                    color: pickLabelColor(BRAND_COLORS.gold),
                  }}
                >
                  {d.case_count}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-[color:var(--page-fg)]">{d.department}</p>
                  <p className="truncate text-[11px] text-[color:var(--page-fg-secondary)]">{d.service_line}</p>
                </div>
                <div className="flex-shrink-0">
                  {d.issue_types.slice(0, 2).map((t) => (
                    <Badge
                      key={t}
                      variant="secondary"
                      className="mr-1 border hairline bg-[color:var(--surface-2)] text-[10px] text-[color:var(--page-fg-secondary)]"
                    >
                      {t}
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Panel>
  );
}

// ---------- KPI strip ----------

export function KpiStrip({ data, loading, onDrill, removedRecords, onRemovedClick }) {
  const cards = [
    {
      label: "Total cases",
      value: data?.total_cases,
      testid: "kpi-total-cases",
      drill: {},
      accent: "var(--page-fg)",
    },
    {
      label: "Open",
      value: data?.open_cases,
      testid: "kpi-open-cases",
      drill: { statuses: ["New", "In Progress", "Under Investigation", "Pending Follow-up", "Escalated"] },
      accent: BRAND_COLORS.blue,
    },
    {
      label: "High-Risk Open",
      value: data?.high_risk_open,
      testid: "kpi-high-risk-open",
      accent: BRAND_COLORS.red,
      badge: data?.high_risk_open > 0 ? <HighRiskBadge className="ml-2" /> : null,
      drill: {
        statuses: ["New", "In Progress", "Under Investigation", "Pending Follow-up", "Escalated"],
        high_risk: true,
      },
    },
    {
      label: "Avg time to resolution",
      value:
        data?.avg_time_to_resolution_days != null
          ? `${data.avg_time_to_resolution_days}d`
          : "—",
      sample: data?.resolution_sample_size
        ? `${data.resolution_sample_size} closed`
        : null,
      testid: "kpi-avg-ttr",
      drill: { statuses: ["Closed"] },
      accent: BRAND_COLORS.lime,
    },
    {
      label: "SLA breaches",
      value: data?.sla_breaches,
      testid: "kpi-sla-breaches",
      accent: data?.sla_breaches > 0 ? BRAND_COLORS.orange : "#F5F5F7",
      sample: data?.sla_days_threshold ? `>${data.sla_days_threshold}d open` : null,
      drill: {
        statuses: ["New", "In Progress", "Under Investigation", "Pending Follow-up", "Escalated"],
        sla_breach: true,
      },
    },
    // Phase 4c — second-row KPIs
    {
      label: "Overdue Investigations",
      value: data?.overdue_investigations,
      testid: "kpi-overdue-investigations",
      accent: BRAND_COLORS.orange,
      sample: data?.sla_escalate_days ? `>${data.sla_escalate_days}d open` : null,
      drill: { overdue: true },
    },
    {
      label: "Follow-up Due Today",
      value: data?.follow_up_due_today,
      testid: "kpi-follow-up-due-today",
      accent: BRAND_COLORS.magenta,
      drill: { follow_up_due: "today" },
    },
    ...(data?.my_open_cases !== null && data?.my_open_cases !== undefined
      ? [
          {
            label: "My Open Cases",
            value: data.my_open_cases,
            testid: "kpi-my-open-cases",
            accent: BRAND_COLORS.blue,
            drill: { mine: true, statuses: ["New", "In Progress", "Under Investigation", "Pending Follow-up", "Escalated"] },
          },
        ]
      : []),
    {
      label: "Stale Cases",
      value: data?.stale_cases,
      testid: "kpi-stale-cases",
      accent: BRAND_COLORS.yellow,
      sample: data?.sla_stale_days ? `no activity >${data.sla_stale_days}d` : null,
      drill: { stale: true },
    },
    // Phase 2 (May 2026) — admin-only Removed Records tile. Same row weight as
    // SLA breaches (slate accent, click-through to /audit-logs). Renders only
    // when the parent passed `removedRecords` (the parent already gates by
    // role + is_admin and skips the fetch for non-admins, so this prop is
    // null for everyone else).
    ...(removedRecords
      ? [
          {
            label: "Removed Records (30d)",
            value: removedRecords.total ?? 0,
            testid: "kpi-removed-records",
            accent: "#475569", // slate
            sample:
              removedRecords.total > 0
                ? `${removedRecords.archived || 0} archived · ${removedRecords.deleted || 0} deleted`
                : "No removals in last 30 days",
            customClick: true,
          },
        ]
      : []),
  ];
  return (
    <div data-testid="kpi-strip" className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5">
      {cards.map((c) => (
        <button
          key={c.label}
          type="button"
          data-testid={c.testid}
          onClick={() => (c.customClick ? onRemovedClick?.() : onDrill?.(c.drill))}
          className="brand-card flex flex-col px-5 py-4 text-left transition-colors hover:bg-[color:var(--surface-2)]"
        >
          <p
            className="text-[10px] font-semibold uppercase tracking-[0.10em]"
            style={{ color: "var(--page-fg-secondary)" }}
          >
            {c.label}
          </p>
          <div className="mt-1 flex items-baseline gap-1">
            <span
              className="text-[28px] font-semibold tabular-nums"
              style={{
                color: loading ? "var(--page-fg-muted)" : "var(--page-fg)",
                letterSpacing: "-0.02em",
              }}
            >
              {loading ? "—" : c.value ?? "—"}
            </span>
            {c.badge}
          </div>
          {c.sample && (
            <p
              className="mt-1 text-[11px] tabular-nums"
              style={{ color: "var(--page-fg-muted)" }}
            >
              {c.sample}
            </p>
          )}
        </button>
      ))}
    </div>
  );
}


// ============================================================
// Phase 4c — Cases-by-Partner / Cases-by-Department widgets
// ============================================================

function _initials(name) {
  const parts = (name || "?").trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || "?").join("");
}

export function CasesByPartnerChart({ data, loading, onDrill }) {
  if (loading) {
    return (
      <Panel title="Cases by HR Partner" subtitle="Top 6 by open caseload" testid="panel-cases-by-partner">
        <LoadingPanel />
      </Panel>
    );
  }
  const items = (data?.items || []).slice(0, 6);
  const max = items.reduce((m, i) => Math.max(m, i.total), 0) || 1;
  return (
    <Panel
      title="Cases by HR Partner"
      subtitle="Top 6 partners — click to drill"
      testid="panel-cases-by-partner"
    >
      {items.length === 0 ? (
        <EmptyPanel />
      ) : (
        <div className="space-y-2">
          {items.map((p) => (
            <button
              key={p.partner_id || p.partner_name}
              data-testid={`partner-row-${p.partner_id || p.partner_name}`}
              onClick={() => p.partner_id && onDrill?.({ assigned_hr_partner_id: p.partner_id })}
              className="flex w-full items-center gap-3 rounded-xl border hairline px-3 py-2 text-left transition-colors hover:bg-[color:var(--surface)]"
              style={{ backgroundColor: "var(--surface-2)" }}
            >
              <span
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold"
                style={{ backgroundColor: BRAND_COLORS.teal, color: "#FFFFFF" }}
              >
                {_initials(p.partner_name)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-[color:var(--page-fg)]">{p.partner_name}</p>
                <div className="relative mt-1 h-2 rounded-full bg-[color:var(--surface-2)]">
                  <div
                    className="absolute inset-y-0 left-0 rounded-full"
                    style={{
                      width: `${(p.open / max) * 100}%`,
                      backgroundColor: BRAND_COLORS.teal,
                      boxShadow: `0 0 8px ${BRAND_COLORS.teal}`,
                    }}
                  />
                  <div
                    className="absolute inset-y-0 rounded-full opacity-50"
                    style={{
                      left: `${(p.open / max) * 100}%`,
                      width: `${(p.closed / max) * 100}%`,
                      backgroundColor: BRAND_COLORS.forest,
                    }}
                  />
                </div>
              </div>
              <div className="flex flex-col items-end text-[11px] leading-tight">
                <span
                  className="font-semibold tabular-nums"
                  style={{ color: "var(--page-fg)" }}
                >
                  <span
                    aria-hidden
                    className="mr-1 inline-block h-1.5 w-1.5 rounded-full align-middle"
                    style={{ backgroundColor: BRAND_COLORS.teal }}
                  />
                  {p.open} open
                </span>
                <span className="tabular-nums text-[color:var(--page-fg-secondary)]">{p.closed} closed</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </Panel>
  );
}

export function CasesByDepartmentChart({ data, loading, onDrill }) {
  if (loading) {
    return (
      <Panel title="Cases by Department" subtitle="Top 8 by total volume" testid="panel-cases-by-department">
        <LoadingPanel />
      </Panel>
    );
  }
  const items = (data?.items || []).slice(0, 8);
  return (
    <Panel
      title="Cases by Department"
      subtitle={items.length ? `${items.length} departments — click to drill` : ""}
      testid="panel-cases-by-department"
    >
      {items.length === 0 ? (
        <EmptyPanel />
      ) : (
        <ResponsiveContainer width="100%" height={Math.max(220, items.length * 28)}>
          <BarChart data={items} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} horizontal={false} />
            <XAxis type="number" tick={{ fill: BRAND_COLORS.muted, fontSize: 11 }} allowDecimals={false} />
            <YAxis type="category" dataKey="department" width={120} tick={{ fill: BRAND_COLORS.textSoft, fontSize: 12 }} />
            <Tooltip content={<TooltipCard />} cursor={{ fill: "rgba(214,177,106,0.08)" }} />
            <Legend iconSize={8} wrapperStyle={{ fontSize: 11, color: BRAND_COLORS.textSoft }} />
            <Bar dataKey="open" name="Open" stackId="a" fill={BRAND_COLORS.teal} radius={[0, 0, 0, 0]} cursor="pointer"
                 onClick={(payload) => payload && onDrill?.({ department: payload.department })} />
            <Bar dataKey="closed" name="Closed" stackId="a" fill={BRAND_COLORS.forest} radius={[0, 8, 8, 0]} cursor="pointer"
                 onClick={(payload) => payload && onDrill?.({ department: payload.department })} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Panel>
  );
}

// ============================================================
// Phase 4c — Outcome Analytics
// ============================================================

export function OutcomesDistributionChart({ data, loading }) {
  if (loading) {
    return <Panel title="Most Common Outcomes" testid="panel-outcomes-dist"><LoadingPanel /></Panel>;
  }
  const items = (data?.items || []).slice(0, 8);
  return (
    <Panel
      title="Most Common Outcomes"
      subtitle={data?.total ? `${data.total} closed cases` : "No closed cases in range"}
      testid="panel-outcomes-dist"
    >
      {items.length === 0 ? (
        <EmptyPanel />
      ) : (
        <ResponsiveContainer width="100%" height={Math.max(200, items.length * 32)}>
          <BarChart data={items} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} horizontal={false} />
            <XAxis type="number" tick={{ fill: BRAND_COLORS.muted, fontSize: 11 }} allowDecimals={false} />
            <YAxis type="category" dataKey="outcome" width={170} tick={{ fill: BRAND_COLORS.textSoft, fontSize: 11 }} />
            <Tooltip content={<TooltipCard />} cursor={{ fill: "rgba(20,184,184,0.08)" }} />
            <Bar dataKey="count" radius={[0, 8, 8, 0]}>
              {items.map((entry, i) => (
                <Cell
                  key={entry.outcome}
                  fill={PIE_PALETTE[i % PIE_PALETTE.length]}
                  data-testid={`outcome-bar-${entry.outcome}`}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </Panel>
  );
}

function _trendChart(data, color, testid, title, subtitle) {
  const series = data?.series || [];
  return (
    <Panel title={title} subtitle={subtitle} testid={testid}>
      {series.length === 0 ? (
        <EmptyPanel />
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={series} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
            <XAxis dataKey="period" tick={{ fill: BRAND_COLORS.muted, fontSize: 11 }} />
            <YAxis tick={{ fill: BRAND_COLORS.muted, fontSize: 11 }} allowDecimals={false} />
            <Tooltip content={<TooltipCard />} />
            <Line
              type="monotone"
              dataKey="count"
              name={title}
              stroke={color}
              strokeWidth={2.5}
              dot={{ r: 4, fill: color, stroke: "var(--surface)", strokeWidth: 2 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Panel>
  );
}

export function TerminationTrendChart({ data, loading }) {
  if (loading) {
    return <Panel title="Termination Trend" testid="panel-termination-trend"><LoadingPanel /></Panel>;
  }
  const total = data?.total || 0;
  return _trendChart(
    data,
    BRAND_COLORS.red,
    "panel-termination-trend",
    "Termination Trend",
    total ? `${total} terminations in window` : "No terminations in range",
  );
}

export function PipTrendChart({ data, loading }) {
  if (loading) {
    return <Panel title="PIP Frequency" testid="panel-pip-trend"><LoadingPanel /></Panel>;
  }
  const total = data?.total || 0;
  return (
    <Panel
      title="PIP Frequency"
      subtitle="Performance Improvement Plans by month"
      testid="panel-pip-trend"
      rightSlot={
        <div
          className="rounded-xl border hairline px-3 py-1.5 text-center"
          style={{ backgroundColor: "rgba(214, 177, 106, 0.12)" }}
        >
          <p className="text-[10px] uppercase tracking-wide text-[color:var(--page-fg-secondary)]">Total</p>
          <p
            className="text-xl font-semibold tabular-nums"
            style={{ color: "var(--page-fg)" }}
            data-testid="pip-total"
          >
            <span
              aria-hidden
              className="mr-1.5 inline-block h-2 w-2 rounded-full align-middle"
              style={{ backgroundColor: BRAND_COLORS.gold }}
            />
            {total}
          </p>
        </div>
      }
    >
      {(data?.series || []).length === 0 ? (
        <EmptyPanel />
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={data.series} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
            <XAxis dataKey="period" tick={{ fill: BRAND_COLORS.muted, fontSize: 11 }} />
            <YAxis tick={{ fill: BRAND_COLORS.muted, fontSize: 11 }} allowDecimals={false} />
            <Tooltip content={<TooltipCard />} />
            <Line
              type="monotone"
              dataKey="count"
              name="PIPs"
              stroke={BRAND_COLORS.gold}
              strokeWidth={2.5}
              dot={{ r: 4, fill: BRAND_COLORS.gold, stroke: "var(--surface)", strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Panel>
  );
}

export function AttendanceHotspotsChart({ data, loading, onDrill }) {
  if (loading) {
    return <Panel title="Attendance Hotspots" testid="panel-attendance-hotspots"><LoadingPanel /></Panel>;
  }
  const items = data?.items || [];
  return (
    <Panel
      title="Attendance Hotspots"
      subtitle={data?.total ? `${data.total} attendance cases · top 5 departments` : "No attendance cases in range"}
      testid="panel-attendance-hotspots"
    >
      {items.length === 0 ? (
        <EmptyPanel />
      ) : (
        <ResponsiveContainer width="100%" height={Math.max(180, items.length * 36)}>
          <BarChart data={items} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} horizontal={false} />
            <XAxis type="number" tick={{ fill: BRAND_COLORS.muted, fontSize: 11 }} allowDecimals={false} />
            <YAxis type="category" dataKey="department" width={130} tick={{ fill: BRAND_COLORS.textSoft, fontSize: 12 }} />
            <Tooltip content={<TooltipCard />} cursor={{ fill: "rgba(247,104,8,0.08)" }} />
            <Bar dataKey="count" radius={[0, 8, 8, 0]}>
              {items.map((entry) => (
                <Cell
                  key={entry.department}
                  fill={BRAND_COLORS.orange}
                  cursor="pointer"
                  data-testid={`hotspot-bar-${entry.department}`}
                  onClick={() => onDrill?.({ issue_types: ["Attendance"], department: entry.department })}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </Panel>
  );
}

// ============================================================
// Phase 4c — HR Partner Performance table
// ============================================================

const HRP_COLUMNS = [
  { key: "partner_name", label: "HR Partner", numeric: false },
  { key: "active_cases", label: "Active", numeric: true },
  { key: "closed_cases", label: "Closed", numeric: true },
  { key: "avg_resolution_days", label: "Avg TTR (d)", numeric: true },
  { key: "follow_up_completion_rate", label: "FU Completion %", numeric: true },
  { key: "auto_escalate_count", label: "Auto-Escalate", numeric: true },
];

export function HRPartnerMetricsTable({ data, loading, onDrillPartner }) {
  const [sortKey, setSortKey] = React.useState("active_cases");
  const [sortDir, setSortDir] = React.useState("desc");

  if (loading) {
    return (
      <Panel title="HR Partner Performance" testid="panel-hr-partner-metrics">
        <LoadingPanel />
      </Panel>
    );
  }

  const items = [...(data?.items || [])].sort((a, b) => {
    const av = a[sortKey];
    const bv = b[sortKey];
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    return sortDir === "asc" ? av - bv : bv - av;
  });

  const handleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  return (
    <Panel
      title="HR Partner Performance"
      subtitle="Click a column header to sort · click a row to drill"
      testid="panel-hr-partner-metrics"
    >
      {items.length === 0 ? (
        <EmptyPanel label="No partners with cases in current filter window." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="hr-partner-metrics-table">
            <thead>
              <tr className="border-b hairline text-left">
                {HRP_COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    data-testid={`hrpm-col-${col.key}`}
                    className={`cursor-pointer select-none px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-[color:var(--page-fg-secondary)] hover:text-[color:var(--page-fg)] ${
                      col.numeric ? "text-right" : "text-left"
                    }`}
                  >
                    {col.label}
                    {sortKey === col.key && (
                      <span className="ml-1 text-[color:var(--page-fg)]">{sortDir === "asc" ? "▲" : "▼"}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr
                  key={row.partner_id}
                  data-testid={`hrpm-row-${row.partner_id}`}
                  onClick={() => onDrillPartner?.(row)}
                  className="cursor-pointer border-b hairline/40 transition-colors last:border-b-0 hover:bg-[color:var(--surface-2)]"
                >
                  <td className="px-3 py-2.5 text-[color:var(--page-fg)]">
                    <div className="flex items-center gap-2">
                      <span
                        className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
                        style={{ backgroundColor: BRAND_COLORS.teal, color: "#FFFFFF" }}
                      >
                        {_initials(row.partner_name)}
                      </span>
                      <span className="font-medium">{row.partner_name}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-right font-semibold tabular-nums" style={{ color: "var(--page-fg)" }}>
                    {row.active_cases}
                  </td>
                  <td className="px-3 py-2.5 text-right text-[color:var(--page-fg-secondary)]">{row.closed_cases}</td>
                  <td className="px-3 py-2.5 text-right text-[color:var(--page-fg-secondary)]">
                    {row.avg_resolution_days != null ? row.avg_resolution_days : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right text-[color:var(--page-fg-secondary)]">
                    {row.follow_up_completion_rate != null
                      ? `${row.follow_up_completion_rate}%`
                      : "—"}
                  </td>
                  <td
                    className="px-3 py-2.5 text-right font-semibold tabular-nums"
                    style={{
                      color:
                        row.auto_escalate_count > 0
                          ? "var(--danger-fg)"
                          : "var(--page-fg)",
                    }}
                  >
                    {row.auto_escalate_count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
